import { createContext, useContext, useState, useEffect } from 'react';

const SiteContext = createContext();

export const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSiteContext must be used within a SiteProvider');
  }
  return context;
};

const SELECTED_SITE_KEY = 'mageguard_selected_site';

export const SiteProvider = ({ children, user }) => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSites = async () => {
    if (!user?.token) return;

    try {
      const response = await fetch('/app/api/sites/list.php', {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const sitesList = data.sites || [];
        setSites(sitesList);

        // Try to restore previously selected site from localStorage
        if (!selectedSite && sitesList.length > 0) {
          const savedSiteId = localStorage.getItem(SELECTED_SITE_KEY);
          const savedSite = savedSiteId ? sitesList.find(s => s.id === parseInt(savedSiteId)) : null;

          if (savedSite) {
            setSelectedSite(savedSite);
          } else if (sitesList.length === 1) {
            // Auto-select only when there's exactly one site (no choice needed)
            setSelectedSite(sitesList[0]);
          }
          // With multiple sites and no saved selection: require explicit user selection
        }
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [user]);

  const selectSite = (site) => {
    setSelectedSite(site);
    if (site?.id) {
      localStorage.setItem(SELECTED_SITE_KEY, site.id.toString());
    } else {
      localStorage.removeItem(SELECTED_SITE_KEY);
    }
  };

  const value = {
    selectedSite,
    sites,
    loading,
    selectSite,
    refreshSites: fetchSites
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};